const WORKFLOW_MUTATION_TOOLS = new Set([
  'automation_insert_node',
  'automation_update_node',
  'workflow_rename',
  'workflow_publish',
]);

export const WORKFLOW_AI_OPERATOR_SCOPE = 'streamlain:workflow-builder';
export const WORKFLOW_AI_OPERATION_EVENT = 'streamlain:workflow-ai-operation';

export interface WorkflowAIOperationDetail {
  phase: 'start' | 'result';
  workflowId?: string;
  targetNodeRef?: string;
  changedNodeNames?: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function changedNodeNamesFromResult(
  data: Record<string, unknown>,
  args: Record<string, unknown>
): string[] {
  const names = new Set<string>();
  const insertedNodeName = stringValue(data['insertedNodeName']);
  if (insertedNodeName) names.add(insertedNodeName);

  const patchOps = Array.isArray(data['patchOps']) ? data['patchOps'] : [];
  for (const patchOp of patchOps) {
    if (!isRecord(patchOp)) continue;
    const nodeName = stringValue(patchOp['nodeName']);
    if (nodeName) names.add(nodeName);
  }

  const explicitNode =
    stringValue(args['nodeId']) ?? stringValue(args['oldName']);
  if (explicitNode) names.add(explicitNode);
  return [...names];
}

function dispatchOperation(detail: WorkflowAIOperationDetail): void {
  window.dispatchEvent(
    new CustomEvent(WORKFLOW_AI_OPERATION_EVENT, { detail })
  );
}

/**
 * Bridges Copilot workflow-authoring stream objects into the open Builder.
 * The backend remains the mutation SSoT; the client only presents each
 * accepted result immediately and humanizes the visible operation.
 */
export function forwardCopilotWorkflowStreamObject(value: unknown): void {
  if (!isRecord(value)) return;
  const type = stringValue(value['type']);
  const toolName = stringValue(value['toolName']);
  if (!toolName || !WORKFLOW_MUTATION_TOOLS.has(toolName)) return;

  const args = isRecord(value['args']) ? value['args'] : {};
  const workflowId = stringValue(args['workflowId']);
  if (type === 'tool-call') {
    dispatchOperation({
      phase: 'start',
      workflowId,
      targetNodeRef:
        stringValue(args['afterNodeId']) ??
        stringValue(args['nodeId']) ??
        stringValue(args['oldName']),
    });
    return;
  }

  if (type !== 'tool-result') return;
  const result = isRecord(value['result']) ? value['result'] : {};
  if (result['success'] === false) return;
  const data = isRecord(result['data']) ? result['data'] : {};
  const updatedWorkflow = isRecord(data['updatedWorkflow'])
    ? data['updatedWorkflow']
    : undefined;
  const resolvedWorkflowId =
    stringValue(updatedWorkflow?.['id']) ??
    stringValue(data['workflowId']) ??
    workflowId;
  const changedNodeNames = changedNodeNamesFromResult(data, args);

  if (updatedWorkflow) {
    window.dispatchEvent(
      new CustomEvent('streamlain:workflow-updated', {
        detail: {
          workflowId: resolvedWorkflowId,
          updatedWorkflow,
          changedNodeNames,
          source: 'copilot',
        },
      })
    );
  }

  dispatchOperation({
    phase: 'result',
    workflowId: resolvedWorkflowId,
    targetNodeRef:
      stringValue(data['insertedNodeName']) ??
      changedNodeNames.at(-1) ??
      stringValue(args['nodeId']),
    changedNodeNames,
  });
}
